"use client";

import { useRef, useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";

const levels = [
    { id: "discover", key: "courses_tab_discover" },
    { id: "beginner", key: "courses_tab_beginner" },
    { id: "intermediate", key: "courses_tab_intermediate" },
    { id: "advanced", key: "courses_tab_advanced" },
];

export default function CoursesTabs({ level, category }) {
    const t = useTranslations();
    const reduceMotion = useReducedMotion();
    const scrollRef = useRef(null);
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    const handleScroll = (e) => {
        const { scrollLeft, scrollWidth, clientWidth } = e.target;
        setIsAtStart(scrollLeft === 0);
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
    };

    // Vérification initiale lors du montage
    useEffect(() => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setIsAtStart(scrollLeft === 0);
            setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
        }
    }, []);

    // Helper pour construire la query avec la catégorie si présente
    const buildQuery = (levelValue) => {
        const query = { level: levelValue };
        if (category) {
            query.category = category;
        }
        return query;
    };

    return (
        <div
            className={`gradient-wrapper relative mt-5 ${
                isAtStart ? "hide-left" : ""
            } ${isAtEnd ? "hide-right" : ""}`}
        >
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex gap-2 overflow-x-auto pb-2 relative"
                style={{ scrollbarWidth: "none" }}
            >
                {levels.map((lvl) => {
                    const isActive = level === lvl.id;

                    return (
                        // Un bouton dans un lien est interdit par le modèle de
                        // contenu HTML, et l'arbre d'accessibilité annonçait
                        // deux fois chaque filtre. Le lien porte donc lui-même
                        // l'apparence. aria-current dit lequel est actif :
                        // jusqu'ici, seule la couleur le signalait.
                        <Link
                            key={lvl.id}
                            href={{
                                pathname: "/courses",
                                query: buildQuery(lvl.id),
                            }}
                            // Changer de filtre ne doit pas faire bouger la page : on reste devant la liste.
                            scroll={false}
                            aria-current={isActive ? "page" : undefined}
                            className={`
                                relative flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
                                transition-colors duration-200 whitespace-nowrap bg-gray-100 dark:bg-white/5
                                ${
                                    isActive
                                        ? "!text-white"
                                        : "text-gray-700 dark:text-grayDark-300 hover:bg-gray-200 dark:hover:bg-white/10"
                                }
                            `}
                        >
                            {/* La pastille active glisse d'un filtre à l'autre au lieu de sauter. */}
                            {isActive && (
                                <motion.span
                                    layoutId="courses-level"
                                    className="absolute inset-0 rounded-xl bg-primary shadow-md shadow-primary/25"
                                    transition={reduceMotion ? { duration: 0 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                />
                            )}
                            <span className="relative">{t(lvl.key)}</span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
